import { ExpensesModel } from '../models/ExpensesModel'
import { Expenses } from '../types/expenses'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { Table } from '../types/components/data-rendering'
import { useFormatDate } from '../composables/formatDate'
import { useTimesheets } from './timesheets'
const { writeExpenses } = ExpensesModel()
const allExpenses = ref<Expenses>([])

export const useExpenses = defineStore('expenses', () => {
  const skip = ref(false)
  watch(allExpenses, (newData) => {
    if (skip.value) {
      skip.value = false
    } else {
      void writeExpenses(newData)
    }
  })
  const currency = computed(() => {
    //Todo: this should come from the user profile settings? or just guess based on locale?
    return 'USD'
  })
  const saveExpenses = (expenses: Expenses, options: { write?: boolean } = { write: true }) => {
    const data: Expenses = expenses.map((expense) => {
      const { date, ...rest } = expense
      return {
        date: new Date(date),
        ...rest,
      }
    })
    if (!options.write) {
      skip.value = true
    }
    allExpenses.value = data
  }
  const saveExpensesByDate = (expenses: Expenses, date: Date) => {
    //flag timesheet by that date as modified by rep
    const { flagAsModifiedByRepByDate } = useTimesheets()
    flagAsModifiedByRepByDate(date)

    allExpenses.value = allExpenses.value.filter(
      (expense) => expense.date.toDateString() !== date.toDateString()
    )
    allExpenses.value = [...expenses, ...allExpenses.value]
  }

  const getTodaysExpenses = computed(() => {
    return allExpenses.value.filter((expense) => {
      return expense.date.toDateString() == new Date().toDateString()
    })
  })

  const getAllExpenses = computed(() => {
    return allExpenses.value
  })

  const getExpensesByDate = (date: Date) => {
    return allExpenses.value.filter((expense) => {
      return expense.date.toDateString() == date.toDateString()
    })
  }

  const getExpensesByDateRange = (range: { start: Date; end: Date }) => {
    return allExpenses.value.filter((expense) => {
      return (
        expense.date.getTime() >= range.start.getTime() &&
        expense.date.getTime() <= range.end.getTime()
      )
    })
  }

  const getTodaysExpensesTotals = computed(() => {
    return getTodaysExpenses.value
      .reduce((acc, curr) => {
        return acc + curr.amount!
      }, 0)
      .toLocaleString(undefined, { style: 'currency', currency: currency.value })
  })

  const getExpenseTotals = (expenses: Expenses) => {
    return expenses
      .reduce((acc, curr) => {
        return acc + curr.amount!
      }, 0)
      .toLocaleString(undefined, { style: 'currency', currency: currency.value })
  }
  const resetStoreState = () => {
    skip.value = true
    allExpenses.value = []
  }
  return {
    saveExpenses,
    saveExpensesByDate,
    getExpensesByDateRange,
    getTodaysExpenses,
    getAllExpenses,
    getTodaysExpensesTotals,
    getExpenseTotals,
    getExpensesByDate,
    resetStoreState,
  }
})

export const formatExpensesForTable = (exp: Expenses): Table => {
  const mappedExp = exp.map((expense) => {
    const date = useFormatDate(new Date(expense.date))
    return {
      date: `${date.weekday()}, ${date.month()} ${date.day()}`,
      description: expense.description!,
      amount: expense.amount!.toLocaleString(undefined, { style: 'currency', currency: 'USD' }),
    }
  })

  return {
    meta: {
      columns: {
        date: {
          label: {
            en_US: 'Date',
            fr_CA: 'Date',
            es_MX: 'Date',
          },
          type: 'string',
        },
        description: {
          label: {
            en_US: 'Description',
            fr_CA: 'Description',
            es_MX: 'Description',
          },
          type: 'string',
        },
        amount: {
          label: {
            en_US: 'Amount',
            fr_CA: 'Amount',
            es_MX: 'Amount',
          },
          type: 'number',
        },
      },
    },
    data: mappedExp,
  }
}
